import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Button } from '../atoms/Button/Button';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { CorrectionPlanText, DoseText, LimitBadge, elementLabel } from './InsightChips';
import type { InsightConfidence, InsightRecommendation } from '../../services/insightService';
import './ExplainabilityModal.css';

function confidenceKey(confidence: InsightConfidence): string {
  if (confidence === 'high') return 'insights.reef.confidenceHigh';
  if (confidence === 'medium') return 'insights.reef.confidenceMedium';
  return 'insights.reef.confidenceLow';
}

export interface ExplainabilityModalProps {
  /** The recommendation to explain; `null` closes the dialog. */
  recommendation: InsightRecommendation | null;
  onClose: () => void;
}

/** Why a recommendation was made: calculation basis, rationale and confidence. */
export function ExplainabilityModal({ recommendation, onClose }: ExplainabilityModalProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      isOpen={recommendation !== null}
      onClose={onClose}
      title={t('insights.reef.explainTitle')}
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {recommendation !== null && (
        <Stack gap="4">
          <Stack gap="2">
            <Text weight="semibold" className="rg-explainability__element">
              {elementLabel(recommendation.element, t)} · {recommendation.productType}
            </Text>
            <DoseText
              amount={recommendation.amount}
              unit={recommendation.unit}
              frequency={recommendation.frequency}
              unknownConcentration={recommendation.unknownConcentration}
              needsConfirmation={recommendation.needsConfirmation}
            />
            <CorrectionPlanText plan={recommendation.correctionPlan} />
            <div>
              <LimitBadge element={recommendation.element} advisoryOnly={recommendation.advisoryOnly} />
            </div>
          </Stack>
          <dl className="rg-explainability__list">
            <dt>{t('insights.reef.calculationBasisLabel')}</dt>
            <dd>{recommendation.calculationBasis}</dd>
            <dt>{t('insights.reef.rationaleLabel')}</dt>
            <dd>{recommendation.rationale}</dd>
            <dt>{t('insights.reef.confidenceLabel')}</dt>
            <dd>{t(confidenceKey(recommendation.confidence))}</dd>
          </dl>
        </Stack>
      )}
    </Dialog>
  );
}

export default ExplainabilityModal;
